/**
 * Statistical report table (統計一覧表).
 *
 * 6 columns: 保険種別 / 続柄 / 総枚数 / 合計金額 / 一部負担金 / 請求金額
 *
 * Mirrors RA2 statistical.blade.php tbody (lines 265-369):
 *   - insurer_type rows × slots
 *   - 合計金額 is blank for Group ② (is_health_insurance=false)
 *   - change flag triggers total/average rows after the row
 *   - footer: ①+②合計, 当月請求分合計, special rows, 返戻金額
 *   - 返戻金額 shows negative numbers for total/miap/invoice, positive cnt
 */

import React from 'react';
import type {
  StatisticalRow,
  StatisticalSlot,
  StatisticalSummaryRow,
  StatisticalTotals,
} from '../types';

const fmt = (v: number | '' | null | undefined): string => {
  if (v === '' || v === null || v === undefined) return '';
  return new Intl.NumberFormat('ja-JP').format(v as number);
};

interface ThProps { children: React.ReactNode; className?: string }
const Th: React.FC<ThProps> = ({ children, className = '' }) => (
  <th className={`border border-gray-300 bg-gray-100 px-3 py-2 text-center text-sm font-normal ${className}`}>
    {children}
  </th>
);

interface TdProps { children?: React.ReactNode; className?: string; colSpan?: number }
const Td: React.FC<TdProps> = ({ children, className = '', colSpan }) => (
  <td colSpan={colSpan} className={`border border-gray-300 px-3 py-2 text-sm ${className}`}>
    {children}
  </td>
);

const SummaryRow: React.FC<{ label: string; row: StatisticalSummaryRow | null; hideTotal?: boolean; isBold?: boolean }> = ({
  label, row, hideTotal = false, isBold = false,
}) => (
  <tr className={isBold ? 'font-semibold' : ''}>
    <Td>{label}</Td>
    <Td />
    <Td className="text-right">{fmt(row?.cnt)}</Td>
    <Td className="text-right">{!hideTotal ? fmt(row?.total) : ''}</Td>
    <Td className="text-right">{fmt(row?.miap)}</Td>
    <Td className="text-right">{fmt(row?.invoice)}</Td>
  </tr>
);

interface Props {
  rows: StatisticalRow[];
  totals: StatisticalTotals | null;
}

const StatisticalReportTable: React.FC<Props> = ({ rows, totals }) => {
  const renderSlotRow = (row: StatisticalRow, slot: StatisticalSlot, isFirst: boolean) => (
    <tr key={`${row.insurer_type_id}-${slot.slot_index}`}>
      <Td className={isFirst ? 'border-t-2 border-t-gray-400' : ''}>
        {isFirst ? row.name : ''}
      </Td>
      <Td className={isFirst ? 'border-t-2 border-t-gray-400' : ''}>
        {slot.title}
      </Td>
      <Td className={`text-right ${isFirst ? 'border-t-2 border-t-gray-400' : ''}`}>
        {fmt(slot.cnt)}
      </Td>
      <Td className={`text-right ${isFirst ? 'border-t-2 border-t-gray-400' : ''}`}>
        {/* Group ② 合計金額 always blank */}
        {row.is_health_insurance ? fmt(slot.total) : ''}
      </Td>
      <Td className={`text-right ${isFirst ? 'border-t-2 border-t-gray-400' : ''}`}>
        {fmt(slot.medical_insurance_accident_price)}
      </Td>
      <Td className={`text-right ${isFirst ? 'border-t-2 border-t-gray-400' : ''}`}>
        {fmt(slot.invoiceprice)}
      </Td>
    </tr>
  );

  const renderTotalRows = (group: number) => {
    const g = group === 1 ? totals?.group1 : totals?.group2;
    if (!g) return null;
    return (
      <>
        {/* 合計 row */}
        <tr className="border-t-2 border-t-gray-400">
          <Td>{g.tl.title}</Td>
          <Td />
          <Td className="text-right">{fmt(g.tl.cnt)}</Td>
          <Td className="text-right">{group === 1 ? fmt(g.tl.total) : ''}</Td>
          <Td className="text-right">{fmt(g.tl.miap)}</Td>
          <Td className="text-right">{fmt(g.tl.invoice)}</Td>
        </tr>
        {/* 平均 row */}
        <tr>
          <Td>{g.av.title}</Td>
          <Td />
          <Td className="text-right">{/* blank */}</Td>
          <Td className="text-right">{group === 1 ? fmt(g.av.total) : ''}</Td>
          <Td className="text-right">{fmt(g.av.miap)}</Td>
          <Td className="text-right">{fmt(g.av.invoice)}</Td>
        </tr>
        {/* spacer */}
        <tr className="bg-gray-200"><Td /><Td /><Td /><Td /><Td /><Td /></tr>
      </>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm font-normal font-[system-ui,_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_'Yu_Gothic_UI',_Meiryo,_sans-serif] [&_tbody_tr:hover]:bg-gray-100">
        <thead>
          <tr>
            <Th>保険種別</Th>
            <Th>続柄</Th>
            <Th>総枚数</Th>
            <Th>合計金額</Th>
            <Th>一部負担金</Th>
            <Th>請求金額</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const uniqueSlots = row.slots.filter((s, idx, arr) =>
              arr.findIndex(x => x.slot_index === s.slot_index) === idx
            );

            return (
              <React.Fragment key={row.insurer_type_id}>
                {uniqueSlots.map((slot, idx) => {
                  // id=25 hides slots 2 and 3 (前期高齢) — matches blade @if condition
                  if (row.insurer_type_id === 25 && (slot.slot_index === 2 || slot.slot_index === 3)) {
                    return null;
                  }
                  return renderSlotRow(row, slot, idx === 0);
                })}

                {/* Trigger summary rows after id=25 (①) or id=24 (②) */}
                {uniqueSlots.some(s => s.change === 1) && renderTotalRows(1)}
                {uniqueSlots.some(s => s.change === 2) && renderTotalRows(2)}
              </React.Fragment>
            );
          })}

          {/* ①+②合計 */}
          {totals && (
            <tr>
              <Td>①＋②合計</Td>
              <Td />
              <Td className="text-right">
                {fmt((totals.group1?.tl?.cnt ?? 0) + (totals.group2?.tl?.cnt ?? 0))}
              </Td>
              <Td className="text-right">
                {fmt((totals.group1?.tl?.total ?? 0) + (totals.group2?.tl?.total ?? 0))}
              </Td>
              <Td className="text-right">
                {fmt((totals.group1?.tl?.miap ?? 0) + (totals.group2?.tl?.miap ?? 0))}
              </Td>
              <Td className="text-right">
                {fmt((totals.group1?.tl?.invoice ?? 0) + (totals.group2?.tl?.invoice ?? 0))}
              </Td>
            </tr>
          )}

          {/* 当月請求分合計 */}
          <SummaryRow label="当月請求分合計" row={totals?.main ?? null} />

          {/* spacer */}
          <tr className="bg-gray-200"><Td /><Td /><Td /><Td /><Td /><Td /></tr>

          {/* 返戻後再請求(内訳)※ */}
          <SummaryRow label="返戻後再請求(内訳)※" row={totals?.returnrecharge ?? null} />
          {/* 月遅れ請求(内訳)※ */}
          <SummaryRow label="月遅れ請求(内訳)※" row={totals?.delaymonthprice ?? null} />
          {/* 合計 */}
          {totals && (
            <SummaryRow
              label="合計"
              row={{
                cnt:     (totals.returnrecharge?.cnt ?? 0) + (totals.delaymonthprice?.cnt ?? 0),
                total:   (totals.returnrecharge?.total ?? 0) + (totals.delaymonthprice?.total ?? 0),
                miap:    (totals.returnrecharge?.miap ?? 0) + (totals.delaymonthprice?.miap ?? 0),
                invoice: (totals.returnrecharge?.invoice ?? 0) + (totals.delaymonthprice?.invoice ?? 0),
              }}
            />
          )}
          {/* ※ fixed note */}
          <tr>
            <Td />
            <Td colSpan={5} className="text-sm text-gray-600">
              ※返戻・月遅れ分は上記①②に含まれておりますが、「当月請求分合計」には含まれておりません。
            </Td>
          </tr>

          {/* 返戻金額 */}
          <SummaryRow label="返戻金額" row={totals?.returnprice ?? null} />
        </tbody>
      </table>
    </div>
  );
};

export default StatisticalReportTable;
